import { SettingsScope } from 'graphql/gql-types';

interface ResolveMyViewSettingsScopeArgs {
	savedScope?: SettingsScope | null;
	storedScopeFromLocalStorage?: SettingsScope | null;
	propertyColumnSettings?: unknown;
	universalColumnSettings?: unknown;
}

interface GetMyViewSettingsTransitionArgs {
	currentScope: SettingsScope;
	nextScope: SettingsScope;
	universalColumnSettings?: unknown;
	propertyColumnSettings?: unknown;
}

interface ShouldSeedUniversalMyViewColumnsArgs {
	settingsScope: SettingsScope;
	hasSeededUniversalColumns: boolean;
	newSystemSettings: {
		myMetrics?: unknown[];
		tabs?: Record<string, unknown>;
	} | null;
	myMetrics?: unknown[] | null;
	tabs?: Record<string, unknown> | null;
}

export function getMyViewSettingsScopeStorageKey(
	propertyId?: string | null,
): string {
	return propertyId
		? `myview_column_scope:${propertyId}`
		: 'myview_column_scope';
}

export function resolveMyViewSettingsScope({
	savedScope,
	storedScopeFromLocalStorage,
	propertyColumnSettings,
	universalColumnSettings,
}: ResolveMyViewSettingsScopeArgs): SettingsScope {
	//Use the specific 'property' scope it it exists for this property
	if (savedScope) return savedScope;

	// If local storage has a cached value, use it.
	if (storedScopeFromLocalStorage) {
		return storedScopeFromLocalStorage;
	}

	// If property has its own column settings, default to 'property' scope.
	// This handles the case where a user may have switched to 'universal' scope
	// but then later added property-specific columns - we want to respect that and
	// switch them back to 'property' scope since they now have property-specific
	// settings.
	if (propertyColumnSettings) {
		return SettingsScope.property;
	}

	// If there are no property-specific settings but there are universal
	// settings, default to 'universal' scope.
	if (universalColumnSettings) {
		return SettingsScope.universal;
	}

	// Default to 'universal' if no scope settings are found.
	return SettingsScope.universal;
}

export function getMyViewSettingsTransition({
	currentScope,
	nextScope,
	universalColumnSettings,
	propertyColumnSettings,
}: GetMyViewSettingsTransitionArgs) {
	const shouldCopyUniversalToProperty =
		currentScope === SettingsScope.universal &&
		nextScope === SettingsScope.property &&
		!!universalColumnSettings &&
		!propertyColumnSettings;

	return {
		shouldCopyUniversalToProperty,
	};
}

export function shouldSeedUniversalMyViewColumns({
	settingsScope,
	hasSeededUniversalColumns,
	newSystemSettings,
	myMetrics,
	tabs,
}: ShouldSeedUniversalMyViewColumnsArgs): boolean {
	if (settingsScope !== SettingsScope.universal) {
		return false;
	}

	if (hasSeededUniversalColumns || newSystemSettings) {
		return false;
	}

	return !!myMetrics?.length || !!tabs;
}
